import { Check, X } from "@phosphor-icons/react";
import { Reveal } from "@/components/motion/Reveal";

/**
 * ContentBlocks — quiet, editorial content variety on service pages.
 * Style intent: same visual weight as body copy, but with clearer rhythm.
 * No heavy card fills, no big icons, no tinted backgrounds.
 *
 * Supported block types:
 *   - { type: "paragraph", text }
 *   - { type: "quote", text, cite? }
 *   - { type: "checklist", title?, items: [] }
 *   - { type: "do_dont", do_title?, dont_title?, do: [], dont: [] }
 *   - { type: "callout", tone?: "info"|"warn", title?, text }
 */
export function ContentBlocks({ blocks = [], lang = "da" }) {
  if (!blocks?.length) return null;
  return (
    <div className="space-y-10" data-testid="content-blocks">
      {blocks.map((b, i) => <Block key={i} block={b} lang={lang} />)}
    </div>
  );
}

function Block({ block, lang }) {
  const type = block.type || "paragraph";

  if (type === "paragraph") {
    return (
      <Reveal>
        <p className="text-muted-foreground font-light leading-relaxed">{block.text}</p>
      </Reveal>
    );
  }

  if (type === "quote") {
    return (
      <Reveal>
        <blockquote
          data-testid="block-quote"
          className="border-l-2 border-border pl-6 py-1"
        >
          <p className="font-heading text-lg md:text-xl font-medium leading-snug tracking-tight text-foreground/95">
            &ldquo;{block.text}&rdquo;
          </p>
          {block.cite && (
            <footer className="mt-2 label-mono text-muted-foreground/60">— {block.cite}</footer>
          )}
        </blockquote>
      </Reveal>
    );
  }

  if (type === "checklist") {
    return (
      <Reveal>
        <div data-testid="block-checklist">
          {block.title && (
            <div className="label-mono mb-4 text-muted-foreground/60">{block.title}</div>
          )}
          <ul className="space-y-2.5">
            {(block.items || []).map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check size={14} weight="bold" className="mt-1.5 shrink-0 text-muted-foreground/70" />
                <span className="text-muted-foreground font-light leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    );
  }

  if (type === "do_dont") {
    const doTitle = block.do_title || (lang === "da" ? "Gør" : "Do");
    const dontTitle = block.dont_title || (lang === "da" ? "Undgå" : "Avoid");
    return (
      <Reveal>
        <div data-testid="block-do-dont" className="grid gap-8 md:grid-cols-2">
          <div>
            <div className="label-mono mb-4 text-muted-foreground/60">{doTitle}</div>
            <ul className="space-y-2.5">
              {(block.do || []).map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check size={14} weight="bold" className="mt-1.5 shrink-0 text-muted-foreground/70" />
                  <span className="text-muted-foreground font-light leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="label-mono mb-4 text-muted-foreground/60">{dontTitle}</div>
            <ul className="space-y-2.5">
              {(block.dont || []).map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <X size={14} weight="bold" className="mt-1.5 shrink-0 text-muted-foreground/40" />
                  <span className="text-muted-foreground/70 font-light leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>
    );
  }

  if (type === "callout") {
    return (
      <Reveal>
        <div
          data-testid="block-callout"
          className="border-l-2 border-border pl-6 py-1"
        >
          {block.title && (
            <div className="font-heading text-base font-bold tracking-tight">{block.title}</div>
          )}
          <p className={`text-muted-foreground font-light leading-relaxed ${block.title ? "mt-2" : ""}`}>
            {block.text}
          </p>
        </div>
      </Reveal>
    );
  }

  return null;
}
